import React, { useState } from "react";
import LeaveTypeSelect from "../../components/Employee/Leave/LeaveTypeSelect";
import FileUpload from "../../components/Employee/OfficialBusiness/FileUpload";
import ReasonField from "../../components/Employee/OfficialBusiness/ReasonField";
import TextInput from "../../components/Reusable/TextInput";
import TimeInput from "../../components/Reusable/TimeInput";
import DateInput from "../../components/Reusable/DateInput";
import SectionDivider from "../../components/Reusable/SectionDivider";
import { useGlobal } from "../../contexts/GlobalContext";
import SelectInput from "../../components/Reusable/SelectInput";
import { useCreateMedia } from "../../hooks/useMedia";
import { combineDateWithTime, EMPLOYEE_MEDIA_REFERENCE } from "../../util/common";
import { useCreateOvertimeRequest } from "../../hooks/useOvertime";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

function OvertimeApplicationLayout() {
  const [form, setForm] = useState({
    day: "",
    shiftDate: "",
    shiftFrom: null,
    shiftTo: null,
    otType: "Normal OT",
    otStart: "",
    otEnd: "",
    reason: "",
    attachment: null,
  });

  const { user } = useGlobal();
  const fileMediaUpload = useCreateMedia();
  const overtimeRequest = useCreateOvertimeRequest();
  const navigate = useNavigate();

  const [fileAttached, setFileAttached] = useState(null);

  const handleShiftDateChange = (val) => {
    const value = val;

    if (
      user &&
      user.ShiftSchedule &&
      user.ShiftSchedule.ShiftSchedules &&
      Array.isArray(user.ShiftSchedule.ShiftSchedules) &&
      user.ShiftSchedule.ShiftSchedules.length > 0
    ) {
      const employeeSchedules = user.ShiftSchedule.ShiftSchedules;

      const parsedDate = new Date(value);

      const _dayOfWeek = parsedDate.toLocaleDateString("en-US", {
        weekday: "long",
      });

      let shiftSchedule = employeeSchedules.find(
        (s) =>
          s.WorkingDays &&
          typeof s.WorkingDays === "string" &&
          s.WorkingDays.toLowerCase() === _dayOfWeek.toLowerCase()
      );

      if (shiftSchedule) {
        setForm((prev) => ({
          ...prev,
          shiftFrom: shiftSchedule.StartTime,
          shiftTo: shiftSchedule.EndTime,
          day: _dayOfWeek,
        }));
      }
      console.log("shiftSchedule: ", shiftSchedule);
    }
  };

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    let validationErrors = {};

    if (!form.shiftDate) validationErrors.shiftDate = "Shift date is required.";
    if (!form.otStart) validationErrors.otStart = "Start time is required.";
    if (!form.otEnd) validationErrors.otEnd = "End time is required.";
    if (!form.reason) validationErrors.reason = "Reason is required.";

    if (form.otStart && form.otEnd && form.otStart >= form.otEnd) {
      validationErrors.timeCompare =
        "Start time must be earlier than end time.";
    }

    // --- Prevent OT during shift hours ---
    if (form.shiftFrom && form.shiftTo && form.otStart && form.otEnd) {
      const shiftFrom = form.shiftFrom;
      const shiftTo = form.shiftTo;
      const otStart = form.otStart;
      const otEnd = form.otEnd;

      const otOverlapsShift =
        (otStart >= shiftFrom && otStart < shiftTo) ||
        (otEnd > shiftFrom && otEnd <= shiftTo) ||
        (otStart <= shiftFrom && otEnd >= shiftTo);

      if (otOverlapsShift) {
        validationErrors.otDuringShift =
          "OT hours should not be within or overlap the shift schedule.";
      }
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleFileUpload = async () => {
    if (!fileAttached) return null;

    const formDataFile = new FormData();
    formDataFile.append("ReferenceType", EMPLOYEE_MEDIA_REFERENCE);
    formDataFile.append("File", fileAttached);

    return new Promise((resolve, reject) => {
      fileMediaUpload.mutate(formDataFile, {
        onSuccess: (data) => {
          if (data.success) {
            resolve(data?.data?.id);
          } else {
            resolve(null);
          }
        },
        onError: (err) => {
          reject(err);
        },
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    let mediaID = null;

    if (fileAttached) {
      mediaID = await handleFileUpload();
    }

    const parsedOTStart = combineDateWithTime(form.shiftDate, form.otStart);
    const parsedOTEnd = combineDateWithTime(form.shiftDate, form.otEnd);

    const totalOtMinutes = Math.floor(
      (parsedOTEnd - parsedOTStart) / (1000 * 60)
    );

    const finalPayload = {
      ...form,
      totalOtMinutes,
      otStart: parsedOTStart.toISOString(),
      otEnd: parsedOTEnd.toISOString(),
      mediaID: mediaID,
    };

    overtimeRequest.mutate(finalPayload, {
      onSuccess: (data) => {
        if (data && data.success) {
          toast.success(data.message);
          return navigate("/OvertimeList");
        }
      },
      onError: (er) => {
        return toast.error("Error occurred");
      },
    });

    console.log("SUBMITTED DATA:", form);
  };

  return (
    <div className="overtime-form-container">
      <h2>Overtime Application Form</h2>

      <form className="overtime-form" onSubmit={handleSubmit}>
        <div className="form-column">
          <TextInput
            label="Day"
            value={form.day}
            placeholder="(auto-filled)"
            readOnly
          />

          <TimeInput label="Shift From" value={form.shiftFrom} readOnly />

          <SectionDivider label={"***"} />

          <SelectInput
            label="Overtime Type"
            choices={[
              { id: "Regular OT", name: "Regular OT" },
              { id: "Early OT", name: "Early OT" },
            ]}
            value={form.otType}
            onChange={(val) => {
              handleChange("otType", val);
            }}
          />
        </div>

        <div className="form-column">
          <DateInput
            label="Shift Date"
            value={form.shiftDate}
            onChange={(val) => {
              handleChange("shiftDate", val);
              handleShiftDateChange(val);
            }}
            error={errors.shiftDate}
          />

          <TimeInput label="Shift To" value={form.shiftTo} readOnly />

          <SectionDivider label={"***"} />

          <TimeInput
            label="Start"
            value={form.otStart}
            onChange={(val) => handleChange("otStart", val)}
            error={errors.otStart}
          />
          <TimeInput
            label="End"
            value={form.otEnd}
            onChange={(val) => handleChange("otEnd", val)}
            error={errors.otEnd}
          />

          {errors.timeCompare && (
            <p className="error-text">{errors.timeCompare}</p>
          )}
          {errors.otDuringShift && (
            <p className="error-text">{errors.otDuringShift}</p>
          )}
        </div>

        <FileUpload onUpload={(file) => setFileAttached(file)} />

        <ReasonField
          fieldName="Overtime Application"
          value={form.reason}
          onChange={(val) => handleChange("reason", val)}
          error={errors.reason}
        />

        <button type="submit" className="submit-btn">
          Submit Application
        </button>
      </form>
    </div>
  );
}

export default OvertimeApplicationLayout;
